import { useState } from "react";
import type { Control, Path, UseFormWatch } from "react-hook-form";
import { Controller } from "react-hook-form";

import {
  ICON_COLOR,
  ICON_SIZE,
  INPUT_VARIANT,
  PropHeroIcon,
  PropHeroText,
  PropHeroTextInput,
  TEXT_COLOR,
  TEXT_TYPOGRAPHY,
  TEXT_WEIGHT,
} from "@prophero-ds/react-components";

type PasswordCopy = {
  passwordLabel: string;
  passwordPlaceholder: string;
  passwordRequired: string;
  passwordDoesNotMeetRequirements: string;
  confirmPasswordLabel: string;
  confirmPasswordPlaceholder: string;
  confirmPasswordRequired: string;
  passwordsDoNotMatch: string;
  ruleLength: string;
  ruleMixCase: string;
  ruleNumber: string;
  ruleMatch: string;
};

type PasswordFieldValues = {
  password?: string;
  confirmPassword?: string;
};

type PasswordFieldsProps<T extends PasswordFieldValues> = {
  control: Control<T>;
  watch: UseFormWatch<T>;
  passwordSDKError?: string;
  isPasswordValid: boolean;
  copy: PasswordCopy;
};

function PasswordFields<T extends PasswordFieldValues>({
  control,
  watch,
  passwordSDKError,
  isPasswordValid,
  copy,
}: PasswordFieldsProps<T>) {
  const passwordFieldName = "password" as Path<T>;
  const confirmPasswordFieldName = "confirmPassword" as Path<T>;
  const passwordValue = watch(passwordFieldName) ?? "";
  const confirmPasswordValue = watch(confirmPasswordFieldName) ?? "";
  const [showPasswordValue, setShowPasswordValue] = useState<boolean>(false);
  const [showConfirmPasswordValue, setShowConfirmPasswordValue] =
    useState<boolean>(false);

  const passwordsMatch =
    passwordValue &&
    confirmPasswordValue &&
    passwordValue === confirmPasswordValue;
  const matchStatus = !confirmPasswordValue
    ? "neutral"
    : passwordsMatch
      ? "valid"
      : "error";

  const lengthStatus = !passwordValue
    ? "neutral"
    : passwordValue.length >= 8
      ? "valid"
      : "error";
  const numberStatus = !passwordValue
    ? "neutral"
    : /[0-9]/.test(passwordValue)
      ? "valid"
      : "error";
  const mixCaseStatus = !passwordValue
    ? "neutral"
    : /[A-Z]/.test(passwordValue) && /[a-z]/.test(passwordValue)
      ? "valid"
      : "error";

  const renderRule = (status: "valid" | "error" | "neutral", text: string) => {
    const isSuccess = status === "valid";
    const isError = status === "error";
    const iconName = isSuccess
      ? "circle-check"
      : isError
        ? "circle-x"
        : "circle";
    const iconColor = isSuccess
      ? ICON_COLOR.SUCCESS
      : isError
        ? ICON_COLOR.ERROR
        : ICON_COLOR.SECONDARY;
    const textColor = isSuccess
      ? TEXT_COLOR.success
      : isError
        ? TEXT_COLOR.error
        : TEXT_COLOR.secondary;

    return (
      <div className="flex items-center gap-1">
        <PropHeroIcon color={iconColor} name={iconName} size={ICON_SIZE.SM} />
        <PropHeroText
          color={textColor}
          typography={TEXT_TYPOGRAPHY["Text-lg"]}
          weight={TEXT_WEIGHT.regular}
        >
          {text}
        </PropHeroText>
      </div>
    );
  };

  return (
    <>
      <Controller
        control={control}
        name={passwordFieldName}
        rules={{
          required: copy.passwordRequired,
          validate: () => {
            if (!isPasswordValid) {
              return copy.passwordDoesNotMeetRequirements;
            }
            return true;
          },
        }}
        render={({ field, fieldState }) => {
          const helperText = fieldState.error?.message ?? passwordSDKError;
          const hasError = !!helperText;

          return (
            <PropHeroTextInput
              label={copy.passwordLabel}
              placeholder={copy.passwordPlaceholder}
              type={showPasswordValue ? "text" : "password"}
              rightIcon={showPasswordValue ? "eye-closed" : "eye"}
              name={field.name}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              helperText={hasError ? String(helperText) : undefined}
              variant={hasError ? INPUT_VARIANT.ERROR : undefined}
              autoComplete="new-password"
              onRightIconClick={() => setShowPasswordValue(!showPasswordValue)}
            />
          );
        }}
      />

      <Controller
        control={control}
        name={confirmPasswordFieldName}
        rules={{
          required: copy.confirmPasswordRequired,
          validate: (value) =>
            value === passwordValue || copy.passwordsDoNotMatch,
        }}
        render={({ field, fieldState }) => {
          const helperText = fieldState.error?.message;
          const hasError = !!helperText;

          return (
            <PropHeroTextInput
              label={copy.confirmPasswordLabel}
              placeholder={copy.confirmPasswordPlaceholder}
              type={showConfirmPasswordValue ? "text" : "password"}
              rightIcon={showConfirmPasswordValue ? "eye-closed" : "eye"}
              name={field.name}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              helperText={hasError ? String(helperText) : undefined}
              variant={hasError ? INPUT_VARIANT.ERROR : undefined}
              autoComplete="new-password"
              onRightIconClick={() =>
                setShowConfirmPasswordValue(!showConfirmPasswordValue)
              }
            />
          );
        }}
      />

      <div className="flex flex-col gap-1">
        {renderRule(lengthStatus, copy.ruleLength)}
        {renderRule(mixCaseStatus, copy.ruleMixCase)}
        {renderRule(numberStatus, copy.ruleNumber)}
        {renderRule(matchStatus, copy.ruleMatch)}
      </div>
    </>
  );
}

export default PasswordFields;
